import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';

import { BehaviorSubject, Subscription, filter } from 'rxjs';

import { AbstractBaseStyleFieldComponent } from '../../BaseStyleField/abstract-base-style-field.component';
import { AbstractPageBuilderService } from '../../../services/abstract-page-builder.service';
import { ButtonTypesEnum } from '../../../../Forms/enums/button-types.enum';
import { DropdownFieldModel } from '../../../models/dropdown-field.model';
import { DropdownOptionModel } from '../../../../Forms/models/dropdown-option.model';
import { ManageStylesDialogComponent } from '../../../../Styles/components/ManageStylesDialog/manage-styles-dialog.component';
import { PaddingModel } from '../../../models/padding.model';
import { SaveStylesDialogComponent } from '../../../../Dialogs/components/SaveStylesDialog/save-styles-dialog.component';
import { SaveTwigTemplateDialogComponent } from '../../../../Dialogs/components/SaveTwigTemplateDialog/save-twig-template-dialog.component';
import { Style } from '../../../../Styles/models/style.model';
import { StylesService } from '../../../../Styles/services/styles.service';
import { StyleStateEnum } from '../../../enums/style-state.enum';
import { Template } from '../../../../Styles/models/template.model';
import { CoreDialogService } from '../../../../../Core/Dialogs/services/dialog.service';
import { FullProfileAnimationModel } from '../../../../../Core/Dialogs/models/full-profile-animation.model';
import { FullProfileDialogAnimationsEnum } from '../../../../../Core/Dialogs/enums/full-profile-dialog-animations.enum';
import { OverlayAnimationStateEnum } from '../../../../../Core/Dialogs/enums/overlay-animation.enum';
import { DialogIoModel } from '../../../../../Core/Dialogs/models/dialog-io.model';
import { BlockDisplayTypeEnum } from '../../../enums/block-display-type.enum';
import { CuratorStyle } from '../../../models/curator-style.model';
import { CuratorStyleFieldModel } from '../../../models/curator-style-field.model';
import { AdvancedColorPickerFieldModel, backgroundColorConfig } from '@Common/PageBuilder/models/advanced-color-picker-field.model';

@Component({
    selector: 'agpr-curator-style-field',
    templateUrl: './curator-style-field.component.html',
    styleUrls: ['./curator-style-field.component.scss'],
    standalone: false
})
export class CuratorStyleFieldComponent extends AbstractBaseStyleFieldComponent implements OnInit, OnChanges, OnDestroy {

    @Input() declare field: CuratorStyleFieldModel;
    @Input() declare value: CuratorStyle;

    @Output() changes: EventEmitter<CuratorStyle> = new EventEmitter<CuratorStyle>();
    styleFormGroup: FormGroup;
    styleStateField: DropdownFieldModel;
    displayRatioDropdownOptions: DropdownOptionModel[] = [];
    selectedRatio: DropdownOptionModel;
    styles: Style[];
    colorPickerField: AdvancedColorPickerFieldModel;
    initted: boolean = false;
    defaultStyleStateOptions: DropdownOptionModel[];

    subscriptions: Subscription[] = [];

    readonly buttonTypes = ButtonTypesEnum;

    constructor(
        public coreDialogService: CoreDialogService,
        public stylesService: StylesService,
        public abstractPageBuilderService: AbstractPageBuilderService
    ) {
        super(coreDialogService);
    }

    ngOnInit(): void {
        if (this.block?.displayElement) {
            this.field.allowExpertMode = this.block?.displayElement?.displayType !== BlockDisplayTypeEnum.ONLINE;
        }

        if (this.field.changeDefaultStyleStateOptions) {
            this.defaultStyleStateOptions = this.field.changeDefaultStyleStateOptions(this);
        }
        this.styleStateField = new DropdownFieldModel(
            null,
            'displayStyleState',
            'PageBuilder.styles.contentBlockStyle',
            '',
            this.defaultStyleStateOptions
        );



        this.colorPickerField =  AdvancedColorPickerFieldModel.create({
             ...backgroundColorConfig( true),
            initialValue: this.value.backgroundColor || '#FFFFFF'
        })



        this.setUpFormGroup();
        this.initted = true;

        this.subscriptions.push(
            this.stylesService.all$.pipe(
                filter(x => !!x)
            ).subscribe(styles => {
                this.styles = styles;
                this.updateStyleOptions();
                this.updateStyleFromSavedStyle();
            }),
            this.styleStateFormControl.valueChanges.subscribe(styleState => this.setStyle(styleState, this.styleStateField.property)),
            this.backgroundColorFormControl.valueChanges.subscribe(backgroundColor => this.setStyle(backgroundColor, 'backgroundColor')),
            this.paddingFormControl.valueChanges.subscribe(padding => this.setStyle(padding, 'padding')),
            this.stylesFormGroup.valueChanges.subscribe(value => {
                this.styleStateFormControl.setValue(this.value['displayStyleState'], { emitEvent: false });
                this.changes.emit(this.value);
            })
        );
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.value && this.initted) {
            this.updateFormGroup();
        }
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach(subscription => subscription.unsubscribe());
    }

    setUpFormGroup(): void {
        this.styleFormGroup = new FormGroup({
            styleState: new FormControl<number | string>(this.value['displayStyleState']),
            styles: new FormGroup({
                backgroundColor: new FormControl<string>(this.value['backgroundColor']),
                padding: new FormControl<PaddingModel>(this.value['padding'] || new PaddingModel())
            })
        });
    }

    /**
     * Opens the save-twig-template-dialog after loading the data needed for it
     */
    public saveExpertTemplate(): void {
        if (!this.styleModel) {
            this.styleModel = new Style();
        }

        this.styleModel.style = this.value;
        this.styleModel.template = this.value.template;
        this.saveTwigTemplateDialogModel.inputs.defaultTemplate = this.abstractPageBuilderService.getDefaultTemplateByBlockType(
            this.abstractPageBuilderService.getBlockTypeEnumFromStyleTypeEnum(this.field.styleType),
            StyleStateEnum.DEFAULT
        );
        // Set the default template if there's not a template on this style already
        if (!this.styleModel.template) {
            this.setTemplate = true;
            this.styleModel.template = new Template();
            this.styleModel.template.template = this.saveTwigTemplateDialogModel.inputs.defaultTemplate;
        }

        this.saveTwigTemplateDialogModel.inputs.type = this.field.styleType;
        this.saveTwigTemplateDialogModel.inputs.inputStyle = this.styleModel;
        this.coreDialogService.open(SaveTwigTemplateDialogComponent, this.saveTwigTemplateDialogModel);
    }

    public saveStyle(): void {
        if (!this.styleModel) {
            this.styleModel = new Style();
        }

        this.styleModel.style = this.value;
        this.styleModel.type = this.field.styleType;

        const io: DialogIoModel = {
            inputs: {
                type: this.field.styleType,
                inputStyle: this.styleModel
            },
            outputs: {
                dialogClose: (response) => {
                    if (response) {
                        this.setStyle(response, 'displayStyleState', true);
                    }
                }
            }
        };

        this.coreDialogService.open(SaveStylesDialogComponent, io);
    }

    manageStylesButtonClicked(){
        const overlayContainer = document.getElementById('main-content');
        const animationTrigger = new BehaviorSubject(
            new FullProfileAnimationModel(
                FullProfileDialogAnimationsEnum.slideInOutAnimation,
                OverlayAnimationStateEnum.open
            )
        );
        const io = {
            inputs: {
                animationTrigger: animationTrigger,
                overlayContainer: overlayContainer
            }
        };
        this.coreDialogService.open(ManageStylesDialogComponent, io);
    }

    protected updateStyleOptions(): void {
        if (!this.styles || !this.value || !this.value.styleState) {
            return;
        }

        if (this.field.changeDefaultStyleStateOptions) {
            this.defaultStyleStateOptions = this.field.changeDefaultStyleStateOptions(this);
        }
        const styleDropdownOptions = this.styles
            .filter(style => style.type === this.field.styleType)
            .map(
                style => {
                    return {
                        label: style.name,
                        value: style.id
                    };
                }
            );

        this.styleStateField.options = [
            ...this.defaultStyleStateOptions,
            ...styleDropdownOptions
        ];
    }

    protected updateStyleFromSavedStyle() {
        if (!this.value || !this.styles || !Number(this.value.styleState)) {
            return;
        }
        const savedStyle: Style = this.styles.find(x => x.id === this.value.styleState as number);
        if (!savedStyle) {
            // the list doesn't contain the id we're looking for, can't load it
            return;
        }
        const curatorStyleModel = new CuratorStyle(savedStyle.style);
        curatorStyleModel.styleState = this.value.styleState;
        curatorStyleModel.displayStyleState = this.value.displayStyleState;
        curatorStyleModel.template = savedStyle.template;

        this.value = curatorStyleModel;

        this.updateFormGroup();
    }

    updateFormGroup(): void {
        this.styleStateFormControl.setValue(this.value['displayStyleState'], { emitEvent: false });
        this.backgroundColorFormControl.setValue(this.value['backgroundColor'], { emitEvent: false });
        this.paddingFormControl.setValue(this.value['padding'] || new PaddingModel(), { emitEvent: false });
    }

    protected displayStyleStateChanged(): void {
        this.styleStateFormControl.setValue(this.value.displayStyleState, { emitEvent: false });
    }

    get styleStateFormControl(): FormControl {
        return this.styleFormGroup.get('styleState') as FormControl;
    }

    get stylesFormGroup(): FormGroup {
        return this.styleFormGroup.get('styles') as FormGroup;
    }

    get backgroundColorFormControl(): FormControl {
        return this.stylesFormGroup.get('backgroundColor') as FormControl;
    }


    get paddingFormControl(): FormControl {
        return this.stylesFormGroup.get('padding') as FormControl;
    }
}
